import { LearningTopic, LearningSession, SessionAttachment, LearningSessionMessage } from '../types';
import { getStoredUser, apiGet, apiPost, apiPut, apiDelete } from './api';
import { parseVideoMetadata } from './videoUtils';

// In-Memory state caches (Starts completely empty)
let inMemoryTopics: LearningTopic[] = [];
let inMemorySessions: LearningSession[] = [];

// Subscribers for changes
const learningSubscribers = new Set<(data: { topics: LearningTopic[]; sessions: LearningSession[] }) => void>();

function notifySubscribers() {
  const data = {
    topics: [...inMemoryTopics],
    sessions: [...inMemorySessions]
  };
  learningSubscribers.forEach(cb => {
    try {
      cb(data);
    } catch (err) {
      console.error('Error notifying learning subscriber:', err);
    }
  });
}

// Synchronously return in-memory caches
export function getLearningTopics(): LearningTopic[] {
  return [...inMemoryTopics];
}

export function getLearningSessions(): LearningSession[] {
  return [...inMemorySessions];
}

/**
 * Completely clears all learning topics and sessions from database
 */
export async function clearAllLearningData(): Promise<void> {
  const user = getStoredUser();
  if (!user) return;

  try {
    const topicsRes = await apiGet<any[]>('/api/learning/topics');
    if (topicsRes.success && Array.isArray(topicsRes.data)) {
      for (const topic of topicsRes.data) {
        await apiDelete(`/api/learning/topics/${topic.id}`);
      }
    }
    inMemoryTopics = [];
    inMemorySessions = [];
    notifySubscribers();
  } catch (err) {
    console.warn('Error clearing learning data:', err);
  }
}

/**
 * Persists current learning store to cloud (Legacy No-Op as we now write incrementally)
 */
export async function saveLearningStoreToCloud(): Promise<void> {
  // Incremental saving is done inside operations now
}

/**
 * Syncs all learning data from server database using hierarchical endpoints.
 */
export async function syncLearningDataFromCloud(): Promise<{ topics: LearningTopic[]; sessions: LearningSession[] }> {
  const user = getStoredUser();
  if (!user) {
    inMemoryTopics = [];
    inMemorySessions = [];
    return { topics: [], sessions: [] };
  }

  try {
    const topicsRes = await apiGet<any[]>('/api/learning/topics');
    if (topicsRes.success && Array.isArray(topicsRes.data)) {
      const loadedTopics: LearningTopic[] = topicsRes.data.map(t => ({
        id: t.id,
        title: t.title || '',
        description: t.description || '',
        createdDate: t.created_at || new Date().toISOString()
      }));

      const loadedSessions: LearningSession[] = [];

      // Fetch sessions, messages, and attachments for each topic in parallel
      await Promise.all(
        loadedTopics.map(async (topic) => {
          const sessionsRes = await apiGet<any[]>(`/api/learning/topics/${topic.id}/sessions`);
          if (sessionsRes.success && Array.isArray(sessionsRes.data)) {
            await Promise.all(
              sessionsRes.data.map(async (s) => {
                // Fetch messages for this session
                const msgsRes = await apiGet<any[]>(`/api/learning/sessions/${s.id}/messages`);
                const messages: LearningSessionMessage[] = (msgsRes.success && Array.isArray(msgsRes.data))
                  ? msgsRes.data.map(m => {
                      let aiMeta = m.ai_info || m.aiInfo ? (typeof (m.ai_info || m.aiInfo) === 'string' ? JSON.parse(m.ai_info || m.aiInfo) : (m.ai_info || m.aiInfo)) : undefined;
                      return {
                        id: m.id,
                        sender: m.sender as 'user' | 'ai' | 'teacher_note',
                        text: m.text || '',
                        timestamp: m.created_at || m.timestamp || new Date().toISOString(),
                        aiInfo: aiMeta
                      };
                    })
                  : [];

                // Fetch attachments for this session
                const attsRes = await apiGet<any[]>(`/api/learning/sessions/${s.id}/attachments`);
                const attachments: SessionAttachment[] = (attsRes.success && Array.isArray(attsRes.data))
                  ? attsRes.data.map(a => {
                      const fileUrl = a.fileUrl || a.file_url || a.r2_url || a.url || '';
                      const isVideo = a.type === 'video' || a.file_type === 'video';
                      const meta = isVideo && fileUrl ? parseVideoMetadata(fileUrl) : null;
                      return {
                        id: a.id,
                        fileId: a.fileId || a.id,
                        key: a.key || a.r2_key,
                        name: a.name || a.file_name || (meta ? meta.suggestedTitle : ''),
                        type: (a.type || a.file_type || (meta ? 'video' : 'other')) as any,
                        size: Number(a.size ?? a.file_size ?? 0),
                        fileSize: a.fileSize || (isVideo ? (meta?.providerName || 'Video Link') : undefined),
                        fileUrl,
                        uploadDate: a.uploadDate || a.created_at || new Date().toISOString(),
                        provider: meta?.provider,
                        videoId: meta?.videoId,
                        embedUrl: meta?.embedUrl,
                        playbackMode: meta?.playbackMode,
                        isExternalLink: Boolean(a.isExternalLink || isVideo)
                      };
                    })
                  : [];

                const sessionAiInfo = s.ai_info || s.aiInfo ? (typeof (s.ai_info || s.aiInfo) === 'string' ? JSON.parse(s.ai_info || s.aiInfo) : (s.ai_info || s.aiInfo)) : undefined;

                loadedSessions.push({
                  id: s.id,
                  topicId: topic.id,
                  sessionTitle: s.session_title || s.title || '',
                  teacherInput: s.teacher_input || '',
                  subTopics: s.sub_topics ? JSON.parse(s.sub_topics) : [],
                  aiNotes: s.ai_notes || '',
                  messages,
                  attachments,
                  exercises: s.exercises || '',
                  quiz: s.quiz || '',
                  createdDate: s.created_at || new Date().toISOString(),
                  aiInfo: sessionAiInfo
                });
              })
            );
          }
        })
      );

      inMemoryTopics = loadedTopics;
      inMemorySessions = loadedSessions;
      notifySubscribers();
    }
  } catch (err) {
    console.warn('Error syncing learning data from cloud:', err);
  }

  return { topics: inMemoryTopics, sessions: inMemorySessions };
}

export const syncLearningDataFromApi = syncLearningDataFromCloud;

/**
 * Subscribe to learning data changes
 */
export function subscribeToLearningData(callback: (data: { topics: LearningTopic[]; sessions: LearningSession[] }) => void): () => void {
  learningSubscribers.add(callback);
  callback({ topics: inMemoryTopics, sessions: inMemorySessions });

  syncLearningDataFromCloud();

  return () => {
    learningSubscribers.delete(callback);
  };
}

// Create Course / Topic
export async function createTopic(title: string, description: string): Promise<LearningTopic> {
  const user = getStoredUser();
  const tempId = `topic_${Date.now()}`;

  const newTopic: LearningTopic = {
    id: tempId,
    title: title.trim(),
    description: description.trim(),
    createdDate: new Date().toISOString(),
  };

  inMemoryTopics.unshift(newTopic);
  notifySubscribers();

  if (user) {
    try {
      const res = await apiPost('/api/learning/topics', {
        title: title.trim(),
        description: description.trim()
      });
      if (res.success && res.data) {
        newTopic.id = res.data.id;
        newTopic.createdDate = res.data.created_at || newTopic.createdDate;
        inMemoryTopics = inMemoryTopics.map(t => t.id === tempId ? { ...newTopic } : t);
        notifySubscribers();
      }
    } catch (err) {
      console.warn('Error saving topic to server:', err);
    }
  }

  return newTopic;
}

// Update Course / Topic
export async function updateTopic(id: string, title: string, description: string): Promise<LearningTopic | null> {
  const idx = inMemoryTopics.findIndex(t => t.id === id);
  if (idx === -1) return null;

  const updated: LearningTopic = {
    ...inMemoryTopics[idx],
    title: title.trim(),
    description: description.trim(),
    updatedDate: new Date().toISOString(),
  };
  inMemoryTopics[idx] = updated;
  notifySubscribers();

  const user = getStoredUser();
  if (user) {
    try {
      await apiPut(`/api/learning/topics/${id}`, {
        title: title.trim(),
        description: description.trim()
      });
    } catch (err) {
      console.warn('Error updating topic on server:', err);
    }
  }

  return updated;
}

// Delete Course / Topic (and all its sessions)
export async function deleteTopic(id: string): Promise<void> {
  inMemoryTopics = inMemoryTopics.filter(t => t.id !== id);
  inMemorySessions = inMemorySessions.filter(s => s.topicId !== id);
  notifySubscribers();

  const user = getStoredUser();
  if (user) {
    try {
      await apiDelete(`/api/learning/topics/${id}`);
    } catch (err) {
      console.warn('Error deleting topic from server:', err);
    }
  }
}

// Create Session inside Course
export async function createSession(
  topicId: string,
  sessionTitle: string,
  teacherInput: string,
  aiNotes: string,
  exercises?: string,
  quiz?: string,
  subTopics?: Array<{ id?: string; title: string; notes: string }>,
  attachments?: SessionAttachment[],
  aiInfo?: { provider?: string; model?: string; responseTime?: number }
): Promise<LearningSession> {
  const createdDate = new Date().toISOString();
  const tempId = `session_${Date.now()}`;
  
  const initialMessages: LearningSession['messages'] = [];
  if (teacherInput.trim()) {
    initialMessages.push({
      id: `msg_init_user_${Date.now()}`,
      sender: 'user',
      text: teacherInput.trim(),
      timestamp: createdDate
    });
  }
  if (aiNotes.trim()) {
    initialMessages.push({
      id: `msg_init_ai_${Date.now()}`,
      sender: 'ai',
      text: aiNotes.trim(),
      timestamp: createdDate,
      aiInfo
    });
  }

  const newSession: LearningSession = {
    id: tempId,
    topicId,
    sessionTitle: sessionTitle.trim(),
    teacherInput: teacherInput.trim(),
    subTopics: subTopics || [],
    aiNotes,
    messages: initialMessages,
    attachments: attachments || [],
    exercises: exercises || '',
    quiz: quiz || '',
    createdDate,
    aiInfo
  };

  inMemorySessions.unshift(newSession);
  notifySubscribers();

  const user = getStoredUser();
  if (user) {
    try {
      const res = await apiPost(`/api/learning/topics/${topicId}/sessions`, {
        sessionTitle: sessionTitle.trim(),
        teacherInput: teacherInput.trim(),
        aiNotes,
        exercises: exercises || '',
        quiz: quiz || '',
        subTopics: JSON.stringify(subTopics || []),
        aiInfo: aiInfo ? JSON.stringify(aiInfo) : undefined
      });

      if (res.success && res.data) {
        const serverSessionId = res.data.id;
        newSession.id = serverSessionId;
        newSession.createdDate = res.data.created_at || newSession.createdDate;

        // Post initial messages to backend if they were created
        if (teacherInput.trim()) {
          await apiPost(`/api/learning/sessions/${serverSessionId}/messages`, {
            sender: 'user',
            text: teacherInput.trim()
          });
        }
        if (aiNotes.trim()) {
          await apiPost(`/api/learning/sessions/${serverSessionId}/messages`, {
            sender: 'ai',
            text: aiNotes.trim(),
            aiInfo: aiInfo ? JSON.stringify(aiInfo) : undefined
          });
        }

        // Post attachments
        if (attachments && attachments.length > 0) {
          for (const att of attachments) {
            await apiPost(`/api/learning/sessions/${serverSessionId}/attachments`, {
              name: att.name,
              type: att.type || 'text/plain',
              size: att.size || 0,
              fileUrl: att.fileUrl || '',
              url: att.fileUrl || '',
              key: att.key || att.fileId || att.id || (att.fileUrl ? `video_link_${Date.now()}` : `link_${Date.now()}`)
            });
          }
        }

        // Re-sync after saving everything to server to get correct IDs
        await syncLearningDataFromCloud();
      }
    } catch (err) {
      console.warn('Error creating session on server:', err);
    }
  }

  return newSession;
}

export async function addAttachmentToSession(
  sessionId: string,
  attachment: SessionAttachment
): Promise<LearningSession | null> {
  const idx = inMemorySessions.findIndex(s => s.id === sessionId);
  if (idx === -1) return null;

  const originalSession = inMemorySessions[idx];
  const existingAttachments = originalSession.attachments || [];
  
  const updatedAttachments = existingAttachments.some(a => a.id === attachment.id)
    ? existingAttachments
    : [...existingAttachments, attachment];

  const updatedSession: LearningSession = {
    ...originalSession,
    attachments: updatedAttachments
  };

  inMemorySessions[idx] = updatedSession;
  notifySubscribers();

  const user = getStoredUser();
  if (user) {
    try {
      const serverRes = await apiPost<any>(`/api/learning/sessions/${sessionId}/attachments`, {
        name: attachment.name,
        type: attachment.type || 'text/plain',
        size: attachment.size || 0,
        fileId: attachment.fileId || attachment.id,
        fileUrl: attachment.fileUrl || '',
        url: attachment.fileUrl || '',
        r2Key: attachment.key || (attachment as any).r2_key || attachment.fileId || '',
        r2_key: attachment.key || (attachment as any).r2_key || attachment.fileId || '',
        key: attachment.key || (attachment as any).r2_key || attachment.fileId || attachment.id || (attachment.fileUrl ? `video_link_${Date.now()}` : `link_${Date.now()}`)
      });
      if (serverRes && serverRes.success && serverRes.data) {
        attachment.id = serverRes.data.id || attachment.id;
        attachment.fileUrl = serverRes.data.fileUrl || serverRes.data.r2_url || attachment.fileUrl;
      }
      await syncLearningDataFromCloud();
    } catch (err) {
      console.warn('Error saving attachment on server:', err);
    }
  }

  return updatedSession;
}

export async function deleteAttachmentFromSession(
  sessionId: string,
  attachmentId: string
): Promise<LearningSession | null> {
  const idx = inMemorySessions.findIndex(s => s.id === sessionId);
  if (idx === -1) return null;

  const originalSession = inMemorySessions[idx];
  const updatedAttachments = (originalSession.attachments || []).filter(a => a.id !== attachmentId && a.fileId !== attachmentId);

  const updatedSession: LearningSession = {
    ...originalSession,
    attachments: updatedAttachments
  };

  inMemorySessions[idx] = updatedSession;
  notifySubscribers();

  const user = getStoredUser();
  if (user) {
    try {
      await apiDelete(`/api/learning/attachments/${attachmentId}`);
    } catch (err) {
      console.warn('Error deleting attachment from server:', err);
    }
  }

  return updatedSession;
}

export async function addMessageToSession(
  sessionId: string,
  message: {
    sender: 'user' | 'ai' | 'teacher_note';
    text: string;
    aiInfo?: { provider?: string; model?: string; responseTime?: number };
  }
): Promise<LearningSession | null> {
  const idx = inMemorySessions.findIndex(s => s.id === sessionId);
  if (idx === -1) return null;

  const originalSession = inMemorySessions[idx];
  const currentMessages = [...(originalSession.messages || [])];

  const newMsg: LearningSessionMessage = {
    id: `msg_${Date.now()}`,
    sender: message.sender,
    text: message.text.trim(),
    timestamp: new Date().toISOString(),
    aiInfo: message.aiInfo
  };

  const updatedMessages = [...currentMessages, newMsg];

  let updatedAiNotes = originalSession.aiNotes || '';
  let updatedTeacherInput = originalSession.teacherInput || '';

  if (message.sender === 'ai') {
    updatedAiNotes = updatedAiNotes ? `${updatedAiNotes}\n\n---\n\n${message.text}` : message.text;
  } else if (message.sender === 'teacher_note') {
    updatedTeacherInput = updatedTeacherInput ? `${updatedTeacherInput}\n\n[یادداشت جدید استاد]: ${message.text}` : `[یادداشت جدید استاد]: ${message.text}`;
  }

  const updatedSession: LearningSession = {
    ...originalSession,
    messages: updatedMessages,
    aiNotes: updatedAiNotes,
    teacherInput: updatedTeacherInput
  };

  inMemorySessions[idx] = updatedSession;
  notifySubscribers();

  const user = getStoredUser();
  if (user) {
    try {
      await apiPost(`/api/learning/sessions/${sessionId}/messages`, {
        sender: message.sender,
        text: message.text.trim(),
        aiInfo: message.aiInfo ? JSON.stringify(message.aiInfo) : undefined
      });
      await syncLearningDataFromCloud();
    } catch (err) {
      console.warn('Error saving message to server:', err);
    }
  }

  return updatedSession;
}

export async function deleteSession(sessionId: string): Promise<void> {
  inMemorySessions = inMemorySessions.filter(s => s.id !== sessionId);
  notifySubscribers();

  const user = getStoredUser();
  if (user) {
    try {
      await apiDelete(`/api/learning/sessions/${sessionId}`);
    } catch (err) {
      console.warn('Error deleting session on server:', err);
    }
  }
}

export async function deleteMessageFromSession(
  sessionId: string,
  messageId: string
): Promise<LearningSession | null> {
  const idx = inMemorySessions.findIndex(s => s.id === sessionId);
  if (idx === -1) return null;

  const originalSession = inMemorySessions[idx];
  const messages = Array.isArray(originalSession.messages) ? originalSession.messages : [];

  const filteredMessages = messages.filter(m => m.id !== messageId);

  const updatedSession: LearningSession = {
    ...originalSession,
    messages: filteredMessages
  };

  inMemorySessions[idx] = updatedSession;
  notifySubscribers();

  const user = getStoredUser();
  if (user) {
    try {
      await apiDelete(`/api/learning/messages/${messageId}`);
    } catch (err) {
      console.warn('Error deleting message from server:', err);
    }
  }

  return updatedSession;
}
